import React from "react"

import { ResponsiveFunnel } from "@nivo/funnel"
import { ResponsivePie } from "@nivo/pie"
import { CheckCircle, FileText, Users } from "lucide-react"
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { ChartContainer, ChartLegend, ChartLegendContent } from "@/components/ui/chart"
import { Skeleton } from "@/components/ui/skeleton"
import { DateFilter } from "@/types/filters"
import { COLORS } from "@/utils/chartColors"

import { useApplicationsBySourceData } from "../../../hooks/useApplicationsBySourceData"
import { useApplicationsPerDayData } from "../../../hooks/useApplicationsPerDayData"
import { useOverviewStatsData } from "../../../hooks/useOverviewStatsData"
import { useVisitorFunnelData } from "../../../hooks/useVisitorFunnelData"
import { MetricCard } from "../MetricCard"

interface OverviewTabProps {
  dateFilter?: DateFilter
  jobId?: string
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ dateFilter, jobId }) => {
  // Fetch visitor funnel data with refetch capability
  const {
    data: visitorFunnelData,
    isLoading: isLoadingFunnel,
    error: funnelError,
  } = useVisitorFunnelData(dateFilter, jobId)

  // Fetch applications per day data
  const {
    data: applicationsPerDayData,
    isLoading: isLoadingApplications,
    error: applicationsError,
  } = useApplicationsPerDayData(dateFilter, jobId)

  // Fetch overview stats data
  const {
    data: overviewStats,
    isLoading: isLoadingStats,
    error: statsError,
  } = useOverviewStatsData(dateFilter, jobId)

  // Fetch applications by source data
  const {
    data: applicationsBySource,
    isLoading: isLoadingSourceData,
    error: sourceDataError,
  } = useApplicationsBySourceData(dateFilter, jobId)

  // Transform visitor funnel data for the Nivo funnel chart
  const visitorFunnelChartData = visitorFunnelData
    ? [
        {
          id: "Visitors",
          label: "Visitors",
          value: visitorFunnelData.totalVisitors,
          color: COLORS[0],
        },
        {
          id: "Started Form",
          label: "Started Form",
          value: visitorFunnelData.startedForm,
          color: COLORS[1],
        },
        {
          id: "Completed Form",
          label: "Completed Form",
          value: visitorFunnelData.completedForm,
          color: COLORS[2],
        },
      ]
    : []

  // Calculate conversion rates for display in the stats section
  const visitorToStartRate =
    visitorFunnelData && visitorFunnelData.totalVisitors > 0
      ? (visitorFunnelData.startedForm / visitorFunnelData.totalVisitors) * 100
      : 0

  const startToCompleteRate =
    visitorFunnelData && visitorFunnelData.startedForm > 0
      ? (visitorFunnelData.completedForm / visitorFunnelData.startedForm) * 100
      : 0

  const visitorToCompleteRate =
    visitorFunnelData && visitorFunnelData.totalVisitors > 0
      ? (visitorFunnelData.completedForm / visitorFunnelData.totalVisitors) * 100
      : 0

  return (
    <div className="space-y-6">
      {/* Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <MetricCard
          title="Total Applications"
          value={overviewStats?.applications.current || 0}
          percentChange={overviewStats?.applications.percentChange || 0}
          icon={<FileText className="h-4 w-4" />}
          isLoading={isLoadingStats}
          error={!!statsError}
        />
        <MetricCard
          title="Unique Visitors"
          value={overviewStats?.uniqueVisitors.current || 0}
          percentChange={overviewStats?.uniqueVisitors.percentChange || 0}
          icon={<Users className="h-4 w-4" />}
          isLoading={isLoadingStats}
          error={!!statsError}
        />
        <MetricCard
          title="Hired Drivers"
          value={overviewStats?.hired.current || 0}
          percentChange={overviewStats?.hired.percentChange || 0}
          icon={<CheckCircle className="h-4 w-4" />}
          isLoading={isLoadingStats}
          error={!!statsError}
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Visitor Funnel Card */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Visitor Conversion Funnel</CardTitle>
            <CardDescription>Visitor to form completion journey</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {isLoadingFunnel ? (
              <div className="flex justify-center items-center">
                <Skeleton className="h-80 w-full" />
              </div>
            ) : funnelError ? (
              <div className="text-center text-red-500">Error loading visitor data</div>
            ) : visitorFunnelData ? (
              <div>
                <div className="h-80 w-full">
                  <ResponsiveFunnel
                    data={visitorFunnelChartData}
                    margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                    colors={{ datum: "color" }}
                    borderWidth={10}
                    labelColor={{ from: "color", modifiers: [["darker", 3]] }}
                    currentPartSizeExtension={4}
                    shapeBlending={0.2}
                    valueFormat={(value) => `${value}`}
                    layers={[
                      "separators",
                      "parts",
                      "labels",
                      "annotations",
                      ({ parts }) => {
                        return parts.map((part, i) => {
                          // Skip the first part (no conversion rate from previous)
                          if (i === 0) return null

                          const previousPart = parts[i - 1]
                          const conversionRate =
                            previousPart.data.value > 0
                              ? ((part.data.value / previousPart.data.value) * 100).toFixed(1)
                              : "0"

                          // Position the text at the beginning of the funnel segment
                          const x = part.x // Slightly offset from the beginning of the segment
                          const y = part.y - 35 // Above the segment

                          return (
                            <g key={`conversion-${i}`}>
                              <text
                                x={x}
                                y={y}
                                textAnchor="middle"
                                dominantBaseline="middle"
                                style={{
                                  fontSize: 12,
                                  fontWeight: "bold",
                                  fill: "#333",
                                }}
                              >
                                {conversionRate}%
                              </text>
                            </g>
                          )
                        })
                      },
                    ]}
                  />
                </div>

                {/* Conversion Rate Stats */}
                <div className="mt-4">
                  <h4 className="text-sm font-medium">Conversion Rates:</h4>
                  <ul className="mt-2 space-y-1 text-sm">
                    <li>
                      Visitor to Form Start:{" "}
                      <span className="font-medium">{visitorToStartRate.toFixed(1)}%</span>
                    </li>
                    <li>
                      Form Start to Completion:{" "}
                      <span className="font-medium">{startToCompleteRate.toFixed(1)}%</span>
                    </li>
                    <li>
                      Overall Conversion:{" "}
                      <span className="font-medium">{visitorToCompleteRate.toFixed(1)}%</span>
                    </li>
                  </ul>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {/* Applications per Day Card */}
        <Card className="col-span-1">
          <CardHeader>
            <CardTitle>Applications per Day</CardTitle>
            <CardDescription>Daily application submission trend</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {isLoadingApplications ? (
              <div className="flex justify-center items-center">
                <Skeleton className="h-80 w-full" />
              </div>
            ) : applicationsError ? (
              <div className="text-center text-red-500">Error loading applications data</div>
            ) : applicationsPerDayData && applicationsPerDayData.length > 0 ? (
              <ChartContainer
                className="h-80 w-full"
                config={{
                  applications: { color: COLORS[4] },
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={applicationsPerDayData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="date"
                      tickFormatter={(date) =>
                        new Date(date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })
                      }
                    />
                    <YAxis allowDecimals={false} domain={[0, "dataMax + 1"]} />
                    <RechartsTooltip
                      labelFormatter={(date) =>
                        new Date(date).toLocaleDateString("en-US", {
                          weekday: "long",
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      }
                      formatter={(value) => [`${value} applications`, "Applications"]}
                    />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Applications"
                      stroke={COLORS[4]}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                    />
                    <ChartLegend>
                      <ChartLegendContent />
                    </ChartLegend>
                  </LineChart>
                </ResponsiveContainer>
              </ChartContainer>
            ) : (
              <div className="flex justify-center items-center h-80">
                <p className="text-palette-jungleGreen500">No application data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Applications by Source</CardTitle>
            <CardDescription>Where candidates are coming from</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            {isLoadingSourceData ? (
              <div className="flex justify-center items-center h-80">
                <Skeleton className="h-80 w-full" />
              </div>
            ) : sourceDataError ? (
              <div className="flex justify-center items-center h-80">
                <p className="text-red-500">Error loading source data</p>
              </div>
            ) : applicationsBySource && applicationsBySource.length > 0 ? (
              <div className="h-80">
                <ResponsivePie
                  data={applicationsBySource.map((entry) => ({
                    id: entry.name,
                    label: entry.name,
                    value: entry.value,
                  }))}
                  margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  activeOuterRadiusOffset={4}
                  colors={({ id }) => {
                    const sourceIndex = applicationsBySource.findIndex((entry) => entry.name === id)
                    return COLORS[(sourceIndex + 3) % COLORS.length]
                  }}
                  borderWidth={1}
                  borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor="#333333"
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: "color" }}
                  arcLabelsSkipAngle={10}
                  arcLabelsTextColor="#ffffff"
                  valueFormat={(value) => {
                    const total = applicationsBySource.reduce((sum, entry) => sum + entry.value, 0)
                    return `${((value / total) * 100).toFixed(1)}%`
                  }}
                  legends={[
                    {
                      anchor: "bottom",
                      direction: "row",
                      justify: false,
                      translateX: 0,
                      translateY: 56,
                      itemsSpacing: 0,
                      itemWidth: 100,
                      itemHeight: 18,
                      itemTextColor: "#999",
                      itemDirection: "left-to-right",
                      itemOpacity: 1,
                      symbolSize: 18,
                      symbolShape: "circle",
                    },
                  ]}
                />
              </div>
            ) : (
              <div className="flex justify-center items-center h-80">
                <p className="text-palette-jungleGreen500">No application source data available</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* <Card className="col-span-1 md:col-span-2">
          <CardHeader>
            <CardTitle>Monthly Hiring Trends</CardTitle>
            <CardDescription>Applications vs. hired candidates</CardDescription>
          </CardHeader>
          <CardContent className="pt-2">
            <ChartContainer
              className="h-80"
              config={{
                applications: { color: COLORS[0] },
                hired: { color: COLORS[1] },
              }}
            >
              <LineChart data={monthlyTrendsData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <RechartsTooltip />
                <Line
                  type="monotone"
                  dataKey="applications"
                  stroke={COLORS[0]}
                />
                <Line type="monotone" dataKey="hired" stroke={COLORS[1]} />
                <ChartLegend>
                  <ChartLegendContent />
                </ChartLegend>
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card> */}
      </div>
    </div>
  )
}

export default OverviewTab
